import { Component, Input, ChangeDetectionStrategy, ViewChild, ElementRef, AfterViewInit, HostListener, OnChanges, SimpleChanges, ChangeDetectorRef, OnDestroy, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';

interface CreditCard {
    id: number;
    type: string;
    lastFourDigits: string;
    remainingBalance: number;
    gradient: string;
    logo?: string;
}

interface Transaction {
    id: number;
    title: string;
    date: string;
    time: string;
    amount: number;
    type: 'debit' | 'credit';
    icon: string; // Icon filename with extension (e.g., 'direction-up.svg')
}

@Component({
    selector: 'app-my-cards-widget',
    standalone: true,
    imports: [CommonModule, WidgetEdit, CdkOverlayOrigin],
    templateUrl: './my-cards.html',
    styleUrls: ['./my-cards.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyCards implements AfterViewInit, OnChanges, OnDestroy {
    @Input() title = 'My Cards';
    @Input() size: 'small' | 'medium' | 'half' | 'full' = 'medium';
    @Input() index = 0;
    @Input() isEditMode = false;
    @Input() isAddMode = false;
    @Output() addWidget = new EventEmitter<void>();
    @Input() activeDropdownIndex: number | null = null;
    @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
    @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
    @Input() changeWidgetSize!: (index: number, size: any) => void;
    @Input() removeWidget!: (index: number) => void;

    @ViewChild('sliderContainer') sliderContainer!: ElementRef;
    @ViewChild('transactionsSection') transactionsSection!: ElementRef;
    sliderWidth = 0;
    cardWidth = 284; // Default card width, will be measured from DOM
    cardGap = 8;     // Default gap, will be measured from DOM
    currentCardIndex = 0;
    transactionKey = 0; // Key to trigger re-render of transaction list
    private resizeObserver: ResizeObserver | null = null;
    private sizeChangeTimeout: any = null; // Store timeout to prevent memory leaks

    constructor(
        private cdr: ChangeDetectorRef,
        private elementRef: ElementRef,
        private router: Router
    ) { }

    @Input() cards: CreditCard[] = [
        {
            id: 1,
            type: 'Credit Card',
            lastFourDigits: '1234',
            remainingBalance: 280050.75,
            gradient: 'linear-gradient(187deg, #E57079 6%, #E7733A 21%, #ECB53E 50%, rgba(255, 255, 255, 0) 100%), white',
            logo: 'assets/icons/global-payment-icon.svg'
        },
        {
            id: 2,
            type: 'Debit Card',
            lastFourDigits: '5678',
            remainingBalance: 125000.50,
            gradient: 'linear-gradient(187deg, #4A90E2 6%, #357ABD 21%, #2E5C8A 50%, rgba(255, 255, 255, 0) 100%), white',
            logo: 'assets/icons/global-payment-icon.svg'
        },
        {
            id: 3,
            type: 'Credit Card',
            lastFourDigits: '9012',
            remainingBalance: 450000.00,
            gradient: 'linear-gradient(187deg, #26BC8F 6%, #1E9B73 21%, #167A5A 50%, rgba(255, 255, 255, 0) 100%), white',
            logo: 'assets/icons/global-payment-icon.svg'
        },
        {
            id: 4,
            type: 'Master Card',
            lastFourDigits: '9012',
            remainingBalance: 850000.00,
            gradient: 'linear-gradient(187deg, #26BC8F 6%, #1E9B73 21%, #167A5A 50%, rgba(255, 255, 255, 0) 100%), white',
            logo: 'assets/icons/global-payment-icon.svg'
        },
        {
            id: 5,
            type: 'Master Card',
            lastFourDigits: '8882',
            remainingBalance: 70000.00,
            gradient: 'linear-gradient(187deg, #26BC8F 6%, #1E9B73 21%, #167A5A 50%, rgba(255, 255, 255, 0) 100%), white',
            logo: 'assets/icons/global-payment-icon.svg'
        }
    ];

    // Define a Map to hold transactions grouped by Card ID
    @Input() transactions: { [cardId: number]: Transaction[] } = {
        1: [ // Transactions for Card ID 1 (Credit Card ****1234)
            { id: 101, title: 'Netflix Subscription', date: '12 Jun', time: '10:00AM', amount: -15.99, type: 'debit', icon: 'img-arrow-right-3.svg' },
            { id: 102, title: 'Apple Store Purchase', date: '13 Jun', time: '02:30PM', amount: -1200.00, type: 'debit', icon: 'img-arrow-right-3.svg' }
        ],
        2: [ // Transactions for Card ID 2 (Debit Card ****5678)
            { id: 201, title: 'Salary Credit', date: '01 Jun', time: '09:00AM', amount: 5000.00, type: 'credit', icon: 'img-arrow-right.svg' },
            { id: 202, title: 'ATM Withdrawal', date: '05 Jun', time: '11:15AM', amount: -200.00, type: 'debit', icon: 'img-arrow-right-3.svg' },
            { id: 203, title: 'Grocery Store', date: '10 Jun', time: '06:45PM', amount: -85.50, type: 'debit', icon: 'img-arrow-right-3.svg' }
        ],
        3: [ // Transactions for Card ID 3 (Credit Card ****9012)
            { id: 301, title: 'Gas Station', date: '14 Jun', time: '08:20AM', amount: -65.00, type: 'debit', icon: 'img-arrow-right-3.svg' },
            { id: 302, title: 'Amazon Refund', date: '15 Jun', time: '12:00PM', amount: 45.99, type: 'credit', icon: 'img-arrow-right.svg' }
        ],
        4: [ // Transactions for Card ID 4 (Master Card ****9012)
            { id: 401, title: 'Flight Ticket', date: '10 Jun', time: '11:00PM', amount: -850.00, type: 'debit', icon: 'img-arrow-right-3.svg' },
            { id: 402, title: 'Hotel Booking', date: '11 Jun', time: '09:00AM', amount: -400.00, type: 'debit', icon: 'img-arrow-right-3.svg' }
        ],
        5: [ // Transactions for Card ID 5 (Master Card ****8882)
            { id: 501, title: 'Coffee Shop', date: '16 Jun', time: '07:30AM', amount: -5.50, type: 'debit', icon: 'img-arrow-right-3.svg' },
            { id: 502, title: 'Freelance Payment', date: '17 Jun', time: '03:00PM', amount: 1200.00, type: 'credit', icon: 'img-arrow-right.svg' }
        ]
    };

    /**
     * Get transactions for the currently active card (limited to 2 transactions max)
     */
    get currentCardTransactions(): Transaction[] {
        const currentCard = this.cards[this.currentCardIndex];
        if (!currentCard) {
            return [];
        }
        const cardTransactions = this.transactions[currentCard.id] || [];
        return cardTransactions.slice(0, 2); // Limit to maximum 2 transactions
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['size']) {
            // Clear any pending timeout to prevent memory leaks
            if (this.sizeChangeTimeout) {
                clearTimeout(this.sizeChangeTimeout);
            }

            // Delay to allow DOM to update after size change
            this.sizeChangeTimeout = setTimeout(() => {
                // handleResize() updates both slider width and transaction scaling
                this.handleResize();
                this.cdr.detectChanges();
                this.sizeChangeTimeout = null;
            }, 100);
        }
    }

    get visibleCards(): CreditCard[] {
        // Show current card and next card for preview
        const cards = [];
        cards.push(this.cards[this.currentCardIndex]);
        if (this.currentCardIndex + 1 < this.cards.length) {
            cards.push(this.cards[this.currentCardIndex + 1]);
        }
        return cards;
    }

    ngAfterViewInit() {
        // Initial resize calculation with delay to ensure DOM is ready
        setTimeout(() => {
            this.handleResize();
        }, 0);

        // Use a single observer on the parent article.widget container
        const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;

        if (!widgetElement) {
            if (!this.isAddMode) {
                console.warn('Could not find parent widget element');
            }
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(() => {
                if (!this.resizeObserver) return;

                const isDragging = document.body.classList.contains('is-resizing');
                const resizingWidgetType = document.body.dataset['resizingWidget'];

                // Update if:
                // 1. Not dragging (toggle, initial load, window resize)
                // 2. Dragging THIS widget specifically
                if (resizingWidgetType === 'mycards') {
                    this.handleResize();
                    this.cdr.detectChanges();
                }
            });
        });
        this.resizeObserver.observe(widgetElement);
    }

    ngOnDestroy() {
        // Clean up ResizeObserver
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // Clear any pending setTimeout to prevent memory leaks
        if (this.sizeChangeTimeout) {
            clearTimeout(this.sizeChangeTimeout);
            this.sizeChangeTimeout = null;
        }
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.handleResize();
    }

    /**
     * Handle all resize events (window resize and widget resize)
     */
    private handleResize() {
        this.updateSliderWidth();
        this.updateTransactionsSectionScale();
    }

    updateSliderWidth() {
        if (this.sliderContainer) {
            this.sliderWidth = this.sliderContainer.nativeElement.offsetWidth;

            // Measure the actual card width and gap from the DOM for stability
            const cardElement = this.sliderContainer.nativeElement.querySelector('.my-cards-widget__card');
            if (cardElement) {
                this.cardWidth = cardElement.offsetWidth;
                const style = window.getComputedStyle(cardElement);
                this.cardGap = parseFloat(style.marginRight) || 8;
            }

            this.cdr.markForCheck();
        }
    }

    /**
     * Update transactions section scale based on widget height AND width
     * Height scaling: 450px to 600px
     * Width scaling: Reduces fonts when width < 370px to prevent cropping
     */
    private updateTransactionsSectionScale() {
        if (!this.transactionsSection) return;

        // Get the parent article.widget element which has the actual height
        const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;
        if (!widgetElement) return;

        const widgetHeight = widgetElement.offsetHeight;
        const widgetWidth = widgetElement.offsetWidth;

        // Height-based scaling
        const minHeight = 450;
        const maxHeight = 600;
        let heightScaleFactor = (widgetHeight - minHeight) / (maxHeight - minHeight);
        heightScaleFactor = Math.max(0, Math.min(1, heightScaleFactor));

        // Width-based scaling (reduce fonts when width is below 370px)
        const normalWidth = 370; // Below this, start reducing fonts
        const minWidth = 250;    // Minimum width where fonts are smallest
        let widthScaleFactor = 1; // Default: no reduction

        if (widgetWidth < normalWidth) {
            // Scale from 0.7 (at 250px) to 1.0 (at 370px)
            widthScaleFactor = 0.7 + (widgetWidth - minWidth) / (normalWidth - minWidth) * 0.3;
            widthScaleFactor = Math.max(0.7, Math.min(1, widthScaleFactor));
        }

        // Apply CSS custom properties for dynamic scaling
        const transactionElement = this.transactionsSection.nativeElement;

        // Font sizes: base + (max - base) * heightScaleFactor, then * widthScaleFactor
        const titleFontSize = (16 + (20 - 16) * heightScaleFactor) * widthScaleFactor;
        const transactionTitleFontSize = (15 + (19 - 15) * heightScaleFactor) * widthScaleFactor;
        const transactionAmountFontSize = (17 + (21 - 17) * heightScaleFactor) * widthScaleFactor;
        const dateFontSize = (12 + (14 - 12) * heightScaleFactor) * widthScaleFactor;

        // Padding and gaps: also affected by width scaling
        const itemPadding = (12 + (18 - 12) * heightScaleFactor) * widthScaleFactor;
        const itemGap = (8 + (14 - 8) * heightScaleFactor) * widthScaleFactor;
        const infoGap = (4 + (8 - 4) * heightScaleFactor) * widthScaleFactor;
        const iconSize = (32 + (40 - 32) * heightScaleFactor) * widthScaleFactor;
        const iconInnerSize = (16 + (20 - 16) * heightScaleFactor) * widthScaleFactor;

        transactionElement.style.setProperty('--tx-title-font-size', `${titleFontSize}px`);
        transactionElement.style.setProperty('--tx-item-title-font-size', `${transactionTitleFontSize}px`);
        transactionElement.style.setProperty('--tx-amount-font-size', `${transactionAmountFontSize}px`);
        transactionElement.style.setProperty('--tx-date-font-size', `${dateFontSize}px`);
        transactionElement.style.setProperty('--tx-item-padding', `${itemPadding}px`);
        transactionElement.style.setProperty('--tx-item-gap', `${itemGap}px`);
        transactionElement.style.setProperty('--tx-info-gap', `${infoGap}px`);
        transactionElement.style.setProperty('--tx-icon-size', `${iconSize}px`);
        transactionElement.style.setProperty('--tx-icon-inner-size', `${iconInnerSize}px`);

        this.cdr.markForCheck();
    }

    getSliderTransform(): string {
        // Use dynamically measured card width and gap for calculation
        const cardTotalWidth = this.cardWidth + this.cardGap;

        // Calculate the offset to center the current card in the slider
        const centerOffset = (this.sliderWidth / 2) - (this.cardWidth / 2);
        const translateX = centerOffset - (this.currentCardIndex * cardTotalWidth);

        return `translateX(${translateX}px)`;
    }

    previousCard(): void {
        if (this.isAddMode) return;
        if (this.currentCardIndex > 0) {
            this.currentCardIndex--;
            this.transactionKey++; // Increment to trigger re-render
            this.cdr.markForCheck(); // Trigger change detection for transactions update
        }
    }

    nextCard(): void {
        if (this.isAddMode) return;
        if (this.currentCardIndex < this.cards.length - 1) {
            this.currentCardIndex++;
            this.transactionKey++; // Increment to trigger re-render
            this.cdr.markForCheck(); // Trigger change detection for transactions update
        }
    }

    canGoPrevious(): boolean {
        return this.currentCardIndex > 0;
    }

    canGoNext(): boolean {
        return this.currentCardIndex < this.cards.length - 1;
    }

    getTransactionIcon(transaction: Transaction): string {
        return transaction.type === 'credit' ? 'arrow-up' : 'arrow-down';
    }

    getTransactionColor(transaction: Transaction): string {
        return transaction.type === 'credit' ? '#26BC8F' : '#EF4D6C';
    }

    getTransactionBgColor(transaction: Transaction): string {
        return transaction.type === 'credit' ? 'rgba(38, 188, 143, 0.15)' : 'rgba(239, 77, 108, 0.15)';
    }

    formatAmount(amount: number): string {
        const formatted = Math.abs(amount).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return amount >= 0 ? `+₹${formatted}` : `₹${formatted}`;
    }

    navigateToTransactions(): void {
        if (this.isEditMode || this.isAddMode) return;
        this.router.navigate(['/transactions']);
    }
}
